import { cn } from "@ui/lib";
import { ChevronRightIcon } from "lucide-react";

/**
 * A JSON tree in Geist Mono: objects and arrays fold (open two levels deep), long strings wrap,
 * and nothing is parsed as HTML. Native `<details>`, so the keyboard opens and closes nodes.
 */
export function JsonView({
	value,
	className,
}: {
	value: unknown;
	className?: string;
}) {
	if (value === null || value === undefined) {
		return (
			<p className={cn("text-muted-foreground text-xs", className)}>
				Nothing was recorded.
			</p>
		);
	}
	return (
		<div
			className={cn(
				"overflow-x-auto rounded-2xl bg-muted/50 p-3 font-mono text-xs leading-relaxed",
				className,
			)}
		>
			<JsonNode value={value} depth={0} />
		</div>
	);
}

function JsonNode({
	name,
	value,
	depth,
}: {
	name?: string;
	value: unknown;
	depth: number;
}) {
	if (value !== null && typeof value === "object") {
		return <JsonBranch name={name} value={value} depth={depth} />;
	}
	return <JsonLeaf name={name} value={value} />;
}

/** "name: " before a value inside an object or array; nothing at the root. */
function JsonLabel({ name }: { name: string | undefined }) {
	return name === undefined ? null : (
		<span className="text-muted-foreground">{name}: </span>
	);
}

/** An object or array: folds, with its size while closed. */
function JsonBranch({
	name,
	value,
	depth,
}: {
	name?: string;
	value: object;
	depth: number;
}) {
	const isArray = Array.isArray(value);
	const entries = isArray
		? value.map((item, index) => [String(index), item] as const)
		: Object.entries(value as Record<string, unknown>);
	const open = isArray ? "[" : "{";
	const close = isArray ? "]" : "}";
	if (entries.length === 0) {
		return (
			<div>
				<JsonLabel name={name} />
				{open}
				{close}
			</div>
		);
	}
	return (
		<details open={depth < 2} className="group">
			<summary className="flex cursor-pointer list-none items-center gap-1 rounded-md outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/40 [&::-webkit-details-marker]:hidden">
				<ChevronRightIcon
					className="size-3 shrink-0 text-muted-foreground transition-transform group-open:rotate-90"
					aria-hidden
				/>
				<span>
					<JsonLabel name={name} />
					{open}
					<span className="text-muted-foreground group-open:hidden">
						{" "}
						{entries.length} {isArray ? "items" : "keys"} {close}
					</span>
				</span>
			</summary>
			<div className="border-border/70 border-l pl-3 ml-1.5">
				{entries.map(([key, item]) => (
					<JsonNode
						key={key}
						name={key}
						value={item}
						depth={depth + 1}
					/>
				))}
			</div>
			<div className="pl-4">{close}</div>
		</details>
	);
}

/** A string, number, boolean or null. */
function JsonLeaf({ name, value }: { name?: string; value: unknown }) {
	return (
		<div className="break-all pl-4">
			<JsonLabel name={name} />
			<span
				className={cn(
					value === null || value === undefined
						? "text-muted-foreground italic"
						: typeof value === "string"
							? "text-foreground"
							: "text-info",
				)}
			>
				{typeof value === "string"
					? JSON.stringify(value)
					: String(value)}
			</span>
		</div>
	);
}
